import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import { useTranslation } from '@michelin/central-provider';
import hash from 'object-hash';
import { PartPrices, PqlPriceType } from 'types/UFOTypes';
import type { SelectedMountingOptionType } from './MountingOptionsModal';
import { OptionSelector } from './OptionSelector';

interface MountingOptionsModalProps {
  qtyToAdd: number;
  selectedOptions: SelectedMountingOptionType[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedMountingOptionType[]>>;
  optionNumber: number;
  prices: { installation?: PartPrices; tpms?: PartPrices; wheels?: PqlPriceType | PartPrices };
}

export function MountingOptionsSelector(props: MountingOptionsModalProps) {
  const { t } = useTranslation();
  const { qtyToAdd, selectedOptions, setSelectedOptions, optionNumber, prices } = props;

  const isComboUSed = (combo: { installation: boolean; tpms: boolean; wheels: boolean }) => {
    let isUsed = false;
    selectedOptions
      .filter((so) => so.added !== false)
      .forEach((option, index) => {
        if (
          hash({ installation: combo.installation, tpms: combo.tpms, wheels: combo.wheels }) ===
            hash({ installation: option.installation, tpms: option.tpms, wheels: option.wheels }) &&
          index !== optionNumber
        )
          isUsed = true;
        if (!combo.installation && !option.installation && index !== optionNumber) isUsed = true;
      });
    return isUsed;
  };

  const getValidCombination = () => {
    if (selectedOptions[optionNumber].added !== false && selectedOptions[optionNumber].installation !== null) {
      return selectedOptions[optionNumber] as { installation: boolean; tpms: boolean; wheels: boolean };
    }
    const combos = [
      { installation: true, wheels: false, tpms: true },
      { installation: true, wheels: false, tpms: false },
      { installation: true, wheels: true, tpms: true },
      { installation: true, wheels: true, tpms: false },
      { installation: false, wheels: false, tpms: false },
    ];
    let validCombo: { installation: boolean; tpms: boolean; wheels: boolean } | null = null;
    combos.forEach((combo) => {
      if (!isComboUSed(combo)) {
        validCombo = combo;
      }
    });
    return validCombo;
  };
  const [selectedOption, setSelectedOption] = useState<{
    installation: boolean;
    wheels: boolean;
    tpms: boolean;
  } | null>(getValidCombination());
  const tabsAdded = selectedOptions.filter((option) => option.added).length;

  useEffect(() => {
    if (selectedOption) {
      const editedSelectedOptions = [...selectedOptions];
      editedSelectedOptions[optionNumber] = { ...selectedOption, added: true, qty: qtyToAdd };
      setSelectedOptions(editedSelectedOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  if (!selectedOption) return null;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography style={{ fontWeight: 'bold' }}>{t('Installation')}</Typography>
          <Typography>{t('Covers the mounting and balancing of the tire onto the wheel rim.')}</Typography>
          <OptionSelector
            optionA={t('None')}
            optionB={t('Mount to Rims')}
            price={
              Number(prices.installation?.invoice_price)
                ? `$${(Number(prices.installation?.invoice_price) * qtyToAdd).toFixed(2)}`
                : ''
            }
            selected={selectedOption.installation}
            disabledA={isComboUSed({ ...selectedOption, installation: false })}
            disabledB={!selectedOption.installation && tabsAdded === 5}
            onSelect={(option: boolean) => {
              if (!option) {
                setSelectedOption({
                  ...selectedOption,
                  installation: false,
                  tpms: false,
                  wheels: false,
                });
              } else {
                setSelectedOption({ ...selectedOption, installation: true });
              }
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography style={{ fontWeight: 'bold' }}>{t('Wheels/Rims')}</Typography>
          <Typography>
            {t('Specify whether you will be providing rims/wheel bases or requesting pricing for rims.')}
          </Typography>
          <OptionSelector
            optionA={t('Use existing rims')}
            optionB={t('New Rims/Wheels')}
            price={
              Number(prices.wheels?.unitAmout)
                ? `$${(Number(prices.tpms?.invoice_price) * qtyToAdd).toFixed(2)}`
                : 'Request price'
            }
            selected={selectedOption.wheels}
            disabledA={!selectedOption.installation || isComboUSed({ ...selectedOption, wheels: false })}
            disabledB={!selectedOption.installation || isComboUSed({ ...selectedOption, wheels: true })}
            onSelect={(option: boolean) => setSelectedOption({ ...selectedOption, wheels: option })}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography style={{ fontWeight: 'bold' }}>{t('TPMS Rebuild Kit')}</Typography>
          <Typography>{t('Includes a valve stem, grommet, and valve stem cap.')}</Typography>
          <OptionSelector
            optionA={t('None')}
            optionB={t('TPMS Rebuild Kit')}
            price={
              Number(prices.tpms?.invoice_price) ? `$${(Number(prices.tpms?.invoice_price) * qtyToAdd).toFixed(2)}` : ''
            }
            selected={selectedOption.tpms}
            disabledA={!selectedOption.installation || isComboUSed({ ...selectedOption, tpms: false })}
            disabledB={!selectedOption.installation || isComboUSed({ ...selectedOption, tpms: true })}
            onSelect={(option: boolean) => setSelectedOption({ ...selectedOption, tpms: option })}
          />
        </Grid>
      </Grid>
    </>
  );
}
